import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import Login from "./components/auth/Login";
import NotFound from "./Not-Found";
import { ProSidebarProvider } from "react-pro-sidebar";
import App from "./App";
import { store } from "./redux/store";
import {
  getDrink,
  getDrinks,
  getFeedbacks,
  getFood,
  getFoodProducts,
  getStaffs,
} from "./helpers/xhr";
import Modal from "./components/modal/Modal";
import { MODAL } from "./components/modal/helpers/constants";
import { RouteAccessError } from "./components/extras/RouteAccessError";
import React, { Fragment, lazy, useLayoutEffect } from "react";
import { UpgradeProvider } from "./components/upgrade/UpgradeContext";
import { ProductType } from "./components/product";
import Loader from "./components/extras/Loader";
import EmailVerification from "./components/auth/EmailVerification";
import ResetPassword from "./components/auth/ForgetPassword";
import { SessionTimeout } from "./components/extras/Session-timeout";
import { RequireAuth } from "./auth/RequireAuth";
import {
  bootstrapDashboard,
  ensureCampaign,
  ensureCombos,
  ensureProductCatalog,
  ensurePromotions,
  requireCmsFeature,
  requireCmsPermission,
} from "./auth/permissionAwareBootstrap";

const ScrollToTop = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);
  return <Fragment>{children}</Fragment>;
};

const Dashboard = lazy(() =>
  import("./pages/dashboard").then((module) => ({ default: module.Dashboard }))
);
const NewProduct = lazy(() =>
  import("./pages/new-product").then((module) => ({
    default: module.NewProduct,
  }))
);
const DrinkPage = lazy(() =>
  import("./pages/drink").then((module) => ({ default: module.DrinkPage }))
);
const Food = lazy(() =>
  import("./pages/food").then((module) => ({ default: module.Food }))
);
const Combo = lazy(() =>
  import("./pages/combo").then((module) => ({
    default: module.ComboPage,
  }))
);
const ComboManagement = lazy(() =>
  import("./pages/combo-management").then((module) => ({
    default: module.ComboManagementPage,
  }))
);
const Order = lazy(() =>
  import("./pages/order").then((module) => ({ default: module.Order }))
);
const Campaign = lazy(() =>
  import("./pages/campaign").then((module) => ({
    default: module.Campaign,
  }))
);

const CampaignManagement = lazy(() =>
  import("./pages/campaign-management").then((module) => ({
    default: module.CampaignManagementPage,
  }))
);
const DiscountPromotions = lazy(() =>
  import("./pages/discount-promotions").then((module) => ({
    default: module.DiscountPromotionsPage,
  }))
);
const StaffManagement = lazy(() =>
  import("./pages/staff-management").then((module) => ({
    default: module.StaffManagement,
  }))
);
const Feedback = lazy(() =>
  import("./pages/feedback").then((module) => ({
    default: module.Feedback,
  }))
);
const Settings = lazy(() =>
  import("./pages/settings").then((module) => ({ default: module.Settings }))
);

const productionDomain = "admin.menutender.com";
const isProductionDomain = window.location.hostname === productionDomain;

const router = createBrowserRouter([
  {
    path: "/",
    element: <Login />,
    errorElement: <NotFound />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
    errorElement: <NotFound />,
  },
  {
    path: "app",
    element: (
      <RequireAuth>
        <UpgradeProvider>
          <ProSidebarProvider>
            <ScrollToTop>
              <App />
            </ScrollToTop>
          </ProSidebarProvider>
        </UpgradeProvider>
      </RequireAuth>
    ),
    children: [
      {
        path: "dashboard",
        element: (
          <React.Suspense fallback={<Loader />}>
            <Dashboard />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsPermission(["dashboard_view", "segment_map_view"]);
          await bootstrapDashboard();
          return null;
        },
      },

      {
        path: "email-verification",
        element: (
          <React.Suspense fallback={<Loader />}>
            <EmailVerification />
          </React.Suspense>
        ),
      },
      {
        path: "order",
        element: (
          <React.Suspense fallback={<Loader />}>
            <Order />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsFeature("orders", ["order_view"]);
          return null;
        },
      },
      {
        path: "combo",
        element: isProductionDomain ? (
          <NotFound />
        ) : (
          <React.Suspense fallback={<Loader />}>
            <Combo />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsFeature("menu_management", ["product_view"]);
          await ensureCombos();
          await ensurePromotions();
          return null;
        },
      },
      {
        path: "combo/management",
        element: isProductionDomain ? (
          <NotFound />
        ) : (
          <React.Suspense fallback={<Loader />}>
            <ComboManagement />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsFeature("menu_management", ["product_write"]);
          await ensureCombos();
          return null;
        },
      },
      {
        path: "campaign",
        element: isProductionDomain ? (
          <NotFound />
        ) : (
          <React.Suspense fallback={<Loader />}>
            <Campaign />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsFeature("promotions", ["promotion_view"]);
          await ensureCampaign();
          return null;
        },
      },
      {
        path: "campaign/management",
        element: isProductionDomain ? (
          <NotFound />
        ) : (
          <React.Suspense fallback={<Loader />}>
            <CampaignManagement />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsFeature("promotions", ["promotion_write"]);
          await ensureCampaign();
          return null;
        },
      },
      {
        path: "promotions/discounts",
        element: (
          <React.Suspense fallback={<Loader />}>
            <DiscountPromotions />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsFeature("promotions", ["promotion_view"]);
          await ensureProductCatalog({ categories: true });
          await ensureCombos();
          await ensurePromotions();
          return null;
        },
      },
      {
        path: "drinks",
        element: (
          <React.Suspense fallback={<Loader />}>
            <DrinkPage />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsFeature("menu_management", ["product_view"]);
          const productState = store.getState().Product;
          const drinks = productState.drinks;
          const categoriesExist = productState.drinkCategories.length;
          if (!drinks.length)
            return getDrinks("Loader", store.dispatch, !!categoriesExist);
          return drinks;
        },
        children: [
          {
            path: ":drinkId",
            element: (
              <Modal
                show={true}
                productType={ProductType.Drink}
                modalModeInit={MODAL.VIEW}
              />
            ),
            loader: ({ params }) => {
              return getDrink(params.drinkId, store.dispatch);
            },
          },
        ],
      },
      {
        path: "food",
        element: (
          <React.Suspense fallback={<Loader />}>
            <Food />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsFeature("menu_management", ["product_view"]);
          const productState = store.getState().Product;
          const categoriesExist = productState.foodCategories.length;
          const food = productState.food;
          if (!food.length)
            return getFoodProducts("Loader", store.dispatch, !!categoriesExist);
          return food;
        },
        children: [
          {
            path: ":foodId",
            element: (
              <Modal
                show={true}
                productType={ProductType.Food}
                modalModeInit={MODAL.VIEW}
              />
            ),
            loader: ({ params }) => {
              return getFood(params.foodId, store.dispatch);
            },
          },
        ],
      },
      {
        path: "add-product",
        element: (
          <React.Suspense fallback={<Loader />}>
            <NewProduct />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsFeature("menu_management", ["product_write"]);
          await ensureProductCatalog({ categories: true });
          return null;
        },
      },
      {
        path: "products/bulk",
        element: <Navigate to="/app/add-product?mode=bulk" replace />,
      },
      {
        path: "staff-management",
        element: (
          <React.Suspense fallback={<Loader />}>
            <StaffManagement />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsFeature("staff_management", ["staff_view"]);
          const { staffs } = store.getState().Staff;
          if (!staffs.length) {
            return getStaffs(store.dispatch);
          }
          return null;
        },
      },
      {
        path: "feedback",
        element: (
          <React.Suspense fallback={<Loader />}>
            <Feedback />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsFeature("feedback");
          const { feedbacks } = store.getState().Feedback;
          if (!feedbacks.length) {
            return getFeedbacks(store.dispatch, 1, 100, true);
          }
          return null;
        },
      },
      {
        path: "settings",
        element: (
          <React.Suspense fallback={<Loader />}>
            <Settings />
          </React.Suspense>
        ),
        loader: async () => {
          await requireCmsPermission(["account_settings"]);
          return null;
        },
      },
    ],
    errorElement: <RouteAccessError />,
  },
  {
    path: "/session-timeout",
    element: <SessionTimeout />,
  },
]);

export default router;
